import { ethers } from "ethers";
import BaseContract from "./BaseContract";
import config from "../config";
import claimvxabi from "../abis/claimvx.json";

interface ClaimVXContractInterface {
  claimBearsDeluxe(tokenIds: number[], forfeit: boolean, payHoney: boolean, value: ethers.BigNumber): Promise<ethers.ContractTransaction>;
  claimWhitelist(amount: number, whitelistedAmount: number, merkleProof: string[], value: ethers.BigNumber): Promise<ethers.ContractTransaction>;
  claimWhitelistWithHoney(amount: number, whitelistedAmount: number, merkleProof: string[]): Promise<ethers.ContractTransaction>;
  publicMint(payHoney: boolean, amount: number, value: ethers.BigNumber): Promise<ethers.ContractTransaction>;
  mintSpares(payHoney: boolean, amount: number, value: ethers.BigNumber): Promise<ethers.ContractTransaction>;
  getClaimedPerWallet(address: string): Promise<number>;
  getCustomVXID(): Promise<number>;
  getAllForfeitIds(): Promise<number[]>;
  getForfeitIndex(): Promise<number>;
  getPrice(): Promise<ethers.BigNumber>;
  getHoneyPrice(): Promise<ethers.BigNumber>;
  getClaimingStatus(): Promise<boolean>;
  getPublicMintStatus(): Promise<boolean>;
  getWhiteListStatus(): Promise<boolean>;
}

class ClaimVXContract extends BaseContract implements ClaimVXContractInterface {
  constructor(signerOrProvider: ethers.Signer | ethers.providers.Provider) {
    super(config.claimVxAddress, claimvxabi, signerOrProvider);
  }

  /**
   * Claim VX for owned bears
   * @param tokenIds - Array of bear token IDs to claim VX for
   * @param forfeit - Whether to forfeit and get a random spare VX instead
   * @param payHoney - Whether to pay with HONEYD instead of ETH
   * @param value - ETH value to send (if not paying with honey)
   */
  async claimBearsDeluxe(
    tokenIds: number[],
    forfeit: boolean,
    payHoney: boolean,
    value: ethers.BigNumber
  ): Promise<ethers.ContractTransaction> {
    if (!this.contract.signer) {
      throw new Error("No signer available. Make sure you're connected to a wallet.");
    }
    const signer = this.contract.signer;
    const contractWithSigner = this.contract.connect(signer);
    
    const gasEstimate = await contractWithSigner.estimateGas.claimBearsDeluxe(
      tokenIds,
      forfeit,
      payHoney,
      { value }
    );
    
    return contractWithSigner.claimBearsDeluxe(
      tokenIds,
      forfeit,
      payHoney,
      { value, gasLimit: gasEstimate.add(100000) }
    );
  }

  /**
   * Claim VX via whitelist with ETH
   */
  async claimWhitelist(
    amount: number,
    whitelistedAmount: number,
    merkleProof: string[],
    value: ethers.BigNumber
  ): Promise<ethers.ContractTransaction> {
    if (!this.contract.signer) {
      throw new Error("No signer available. Make sure you're connected to a wallet.");
    }
    const signer = this.contract.signer;
    const contractWithSigner = this.contract.connect(signer);
    
    const gasEstimate = await contractWithSigner.estimateGas.claimWhitelist(
      amount,
      whitelistedAmount,
      merkleProof,
      { value }
    );
    
    return contractWithSigner.claimWhitelist(
      amount,
      whitelistedAmount,
      merkleProof,
      { value, gasLimit: gasEstimate.add(100000) }
    );
  }

  /**
   * Claim VX via whitelist with HONEYD
   */
  async claimWhitelistWithHoney(
    amount: number,
    whitelistedAmount: number,
    merkleProof: string[]
  ): Promise<ethers.ContractTransaction> {
    if (!this.contract.signer) {
      throw new Error("No signer available. Make sure you're connected to a wallet.");
    }
    const signer = this.contract.signer;
    const contractWithSigner = this.contract.connect(signer);
    
    const gasEstimate = await contractWithSigner.estimateGas.claimWhitelistWithHoney(
      amount,
      whitelistedAmount,
      merkleProof
    );
    
    return contractWithSigner.claimWhitelistWithHoney(
      amount,
      whitelistedAmount,
      merkleProof,
      { gasLimit: gasEstimate.add(100000) }
    );
  }

  /**
   * Public mint VX
   * @param payHoney - Whether to pay with HONEYD instead of ETH
   * @param amount - Number of VX to mint
   * @param value - ETH value to send (if not paying with honey)
   */
  async publicMint(
    payHoney: boolean,
    amount: number,
    value: ethers.BigNumber
  ): Promise<ethers.ContractTransaction> {
    if (!this.contract.signer) {
      throw new Error("No signer available. Make sure you're connected to a wallet.");
    }
    const signer = this.contract.signer;
    const contractWithSigner = this.contract.connect(signer);
    
    const gasEstimate = await contractWithSigner.estimateGas.publicMint(
      payHoney,
      amount,
      { value }
    );
    
    return contractWithSigner.publicMint(
      payHoney,
      amount,
      { value, gasLimit: gasEstimate.add(100000) }
    );
  }

  /**
   * Mint spare/forfeited VX
   */
  async mintSpares(
    payHoney: boolean,
    amount: number,
    value: ethers.BigNumber
  ): Promise<ethers.ContractTransaction> {
    if (!this.contract.signer) {
      throw new Error("No signer available. Make sure you're connected to a wallet.");
    }
    const signer = this.contract.signer;
    const contractWithSigner = this.contract.connect(signer);
    
    const gasEstimate = await contractWithSigner.estimateGas.mintSpares(
      payHoney,
      amount,
      { value }
    );
    
    return contractWithSigner.mintSpares(
      payHoney,
      amount,
      { value, gasLimit: gasEstimate.add(100000) }
    );
  }

  /**
   * Get number of VX claimed by a wallet
   */
  async getClaimedPerWallet(address: string): Promise<number> {
    const claimed = await this.contract.claimedPerWallet(address);
    return claimed.toNumber();
  }

  /**
   * Get the current custom VX ID counter
   */
  async getCustomVXID(): Promise<number> {
    const customVX = await this.contract.customVX();
    return customVX.toNumber();
  }

  /**
   * Get all forfeited VX IDs available for claiming
   */
  async getAllForfeitIds(): Promise<number[]> {
    const ids = await this.contract.getAllForfeithIds();
    return ids.map((id: ethers.BigNumber) => id.toNumber());
  }

  /**
   * Get the current forfeit index
   */
  async getForfeitIndex(): Promise<number> {
    const index = await this.contract.forfeitIndex();
    return index.toNumber();
  }

  /**
   * Get the ETH price for minting
   */
  async getPrice(): Promise<ethers.BigNumber> {
    return this.contract.price();
  }

  /**
   * Get the HONEYD price for minting
   */
  async getHoneyPrice(): Promise<ethers.BigNumber> {
    return this.contract.honeyPrice();
  }

  /**
   * Check if bear claiming is open
   */
  async getClaimingStatus(): Promise<boolean> {
    return this.contract.getClaimingStatus();
  }

  /**
   * Check if public mint is open
   */
  async getPublicMintStatus(): Promise<boolean> {
    return this.contract.getPublicMintStatus();
  }

  /**
   * Check if whitelist mint is open
   */
  async getWhiteListStatus(): Promise<boolean> {
    return this.contract.getWhiteListStatus();
  }
}

export default ClaimVXContract;
